import { useEffect, useMemo, useState, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { FileText, Image, Search } from "lucide-react";
import { ModalWithTabs, type Tab } from "@admin/components/ui/ModalWithTabs";
import { CategorySelect, CustomSelect, ImageUpload } from "@shared/components";
import type { UploadedImage } from "@shared/components/ImageUpload";
import { useActiveCatalogs } from "@shared/hooks/useCatalogs";
import { useCategoryOptions } from "@shared/hooks/useCategoryTree";
import type { CreateCategoryData, Category } from "@admin/types/category";
import {
  generateSeoTitle,
  generateSeoDescription,
  generateSeoKeywords,
} from "@shared/utils/seoUtils";

const optionalString = (max: number, message: string) =>
  z
    .string()
    .max(max, message)
    .transform((value) => {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    })
    .optional();

const createCategorySchema = z.object({
  name: z
    .string()
    .min(2, "El nombre debe tener al menos 2 caracteres")
    .max(150, "El nombre no puede exceder 150 caracteres"),
  catalogId: z.string().uuid("Selecciona un catálogo válido"),
  parentId: z
    .union([
      z.string().uuid("Selecciona una categoría válida"),
      z.literal(""),
      z.null(),
    ])
    .optional(),
  description: optionalString(
    500,
    "La descripción no puede exceder 500 caracteres"
  ),
  image: z
    .union([
      z
        .string()
        .url("La imagen debe ser una URL válida")
        .max(500, "La URL no puede exceder 500 caracteres"),
      z.literal(""),
    ])
    .optional(),
  order: z.coerce
    .number()
    .min(0, "El orden debe ser mayor o igual a 0")
    .max(9999, "El orden no puede exceder 9999")
    .default(0),
  featured: z.boolean().default(false),
  seoTitle: optionalString(60, "El título SEO no puede exceder 60 caracteres"),
  seoDescription: optionalString(
    160,
    "La descripción SEO no puede exceder 160 caracteres"
  ),
  seoKeywords: optionalString(
    200,
    "Las palabras clave SEO no pueden exceder 200 caracteres"
  ),
});

type CreateCategoryFormValues = z.input<typeof createCategorySchema>;
type CreateCategoryFormOutput = z.output<typeof createCategorySchema>;

interface CreateCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCategoryData) => void;
  category?: Category;
  isLoading?: boolean;
}

const defaultValues: CreateCategoryFormValues = {
  name: "",
  catalogId: "",
  parentId: "",
  description: "",
  image: "",
  order: 0,
  featured: false,
  seoTitle: "",
  seoDescription: "",
  seoKeywords: "",
};

export function CreateCategoryModal({
  isOpen,
  onClose,
  onSubmit,
  category,
  isLoading = false,
}: CreateCategoryModalProps) {
  const isEditing = !!category;
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
  } = useForm<CreateCategoryFormValues>({
    resolver: zodResolver(createCategorySchema),
    defaultValues,
    mode: "onChange",
  });

  const selectedCatalogId = watch("catalogId");
  const watchedParentId = watch("parentId");
  const watchedName = watch("name");
  const watchedDescription = watch("description");

  const [autoSeoEnabled, setAutoSeoEnabled] = useState(true);
  const [autoTitle, setAutoTitle] = useState(true);
  const [autoDescription, setAutoDescription] = useState(true);
  const [autoKeywords, setAutoKeywords] = useState(true);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);

  const { data: catalogsData, isLoading: catalogsLoading } =
    useActiveCatalogs();
  const catalogOptions = useMemo(() => {
    const items =
      catalogsData?.data && "items" in catalogsData.data
        ? catalogsData.data.items
        : [];
    return items.map((catalog) => ({ value: catalog.id, label: catalog.name }));
  }, [catalogsData]);

  const { options: rawParentOptions, isLoading: parentLoading } =
    useCategoryOptions(selectedCatalogId || undefined);

  const parentOptions = useMemo(
    () => [
      { value: "", label: "Sin categoría padre", isRoot: true },
      ...rawParentOptions,
    ],
    [rawParentOptions]
  );

  useEffect(() => {
    if (!selectedCatalogId) {
      setValue("parentId", "");
    }
  }, [selectedCatalogId, setValue]);

  useEffect(() => {
    const url = uploadedImages.length > 0 ? uploadedImages[0].url : "";
    setValue("image", url);
  }, [uploadedImages, setValue]);

  // Cargar datos cuando se abre el modal en modo edición
  useEffect(() => {
    if (isOpen && category) {
      const formValues: CreateCategoryFormValues = {
        name: category.name || "",
        catalogId: category.catalogId || "",
        parentId: category.parentId || "",
        description: category.description || "",
        image: category.image || "",
        order: category.order || 0,
        featured: category.featured || false,
        seoTitle: category.seoTitle || "",
        seoDescription: category.seoDescription || "",
        seoKeywords: category.seoKeywords || "",
      };
      reset(formValues);

      // Pre-cargar imagen si existe
      if (category.image) {
        setUploadedImages([
          {
            id: `existing-${category.id}`,
            url: category.image,
            publicId: "",
            fileName: "Imagen existente",
          },
        ]);
      } else {
        setUploadedImages([]);
      }

      // Desactivar auto-SEO en modo edición para no sobrescribir valores existentes
      setAutoSeoEnabled(false);
      setAutoTitle(false);
      setAutoDescription(false);
      setAutoKeywords(false);
    } else if (isOpen && !category) {
      // Modo creación: resetear a valores por defecto
      reset(defaultValues);
      setUploadedImages([]);
      setAutoSeoEnabled(true);
      setAutoTitle(true);
      setAutoDescription(true);
      setAutoKeywords(true);
    }
  }, [isOpen, category, reset, setValue]);

  const extractPlainText = useCallback((text?: string | null) => {
    if (!text) return "";
    return text.replace(/\s+/g, " ").trim();
  }, []);

  const generateAutoSeo = useCallback(() => {
    if (!autoSeoEnabled) return;

    const catalogName =
      catalogOptions.find((option) => option.value === selectedCatalogId)
        ?.label ?? "";
    const parentName =
      parentOptions.find((option) => option.value === watchedParentId)?.label ??
      "";

    if (autoTitle && watchedName) {
      const seoTitle = generateSeoTitle({
        name: watchedName,
        brand: catalogName || parentName || undefined,
      });
      setValue("seoTitle", seoTitle);
    }

    if (autoDescription) {
      const plain = extractPlainText(watchedDescription);
      if (plain) {
        const seoDescription = generateSeoDescription({ description: plain });
        setValue("seoDescription", seoDescription);
      }
    }

    if (autoKeywords && watchedName) {
      const seoKeywords = generateSeoKeywords({
        name: watchedName,
        brand: catalogName || undefined,
        categoryName: parentName || undefined,
        extra: ["categoría", "productos"],
      });
      setValue("seoKeywords", seoKeywords);
    }
  }, [
    autoSeoEnabled,
    autoTitle,
    autoDescription,
    autoKeywords,
    watchedName,
    watchedDescription,
    watchedParentId,
    catalogOptions,
    parentOptions,
    selectedCatalogId,
    extractPlainText,
    setValue,
  ]);

  useEffect(() => {
    const timeout = setTimeout(generateAutoSeo, 300);
    return () => clearTimeout(timeout);
  }, [generateAutoSeo]);

  const handleSeoFieldChange = (
    field: "seoTitle" | "seoDescription" | "seoKeywords"
  ) => {
    if (field === "seoTitle") setAutoTitle(false);
    if (field === "seoDescription") setAutoDescription(false);
    if (field === "seoKeywords") setAutoKeywords(false);
  };

  const handleFormSubmit = (formData: CreateCategoryFormValues) => {
    const parsed: CreateCategoryFormOutput =
      createCategorySchema.parse(formData);

    const payload: CreateCategoryData = {
      name: parsed.name.trim(),
      catalogId: parsed.catalogId,
      order: parsed.order,
      featured: parsed.featured,
    };

    // Solo incluir campos opcionales si tienen valor (no vacíos)
    if (parsed.description && parsed.description.trim() !== "") {
      payload.description = parsed.description.trim();
    }

    if (parsed.image && parsed.image.trim() !== "") {
      payload.image = parsed.image.trim();
    }

    if (parsed.seoTitle && parsed.seoTitle.trim() !== "") {
      payload.seoTitle = parsed.seoTitle.trim();
    }

    if (parsed.seoDescription && parsed.seoDescription.trim() !== "") {
      payload.seoDescription = parsed.seoDescription.trim();
    }

    if (parsed.seoKeywords && parsed.seoKeywords.trim() !== "") {
      payload.seoKeywords = parsed.seoKeywords.trim();
    }

    if (parsed.parentId && parsed.parentId !== "") {
      payload.parentId = parsed.parentId;
    }

    onSubmit(payload);

    // Solo resetear si no estamos editando (el cierre lo maneja el padre)
    if (!isEditing) {
      reset(defaultValues);
      setAutoSeoEnabled(true);
      setAutoTitle(true);
      setAutoDescription(true);
      setAutoKeywords(true);
      setUploadedImages([]);
    }
  };

  const handleClose = () => {
    reset(defaultValues);
    setAutoSeoEnabled(true);
    setAutoTitle(true);
    setAutoDescription(true);
    setAutoKeywords(true);
    setUploadedImages([]);
    onClose();
  };

  // Definir los tabs
  const tabs: Tab[] = [
    {
      id: "general",
      label: "General",
      icon: <FileText className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Nombre de la Categoría <span className="text-red-500">*</span>
              </label>
              <input
                {...register("name")}
                type="text"
                id="name"
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                  errors.name
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Ej: Pisos Interiores"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Catálogo <span className="text-red-500">*</span>
              </label>
              <Controller
                control={control}
                name="catalogId"
                render={({ field }) => (
                  <CustomSelect
                    options={catalogOptions}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      setValue("parentId", "");
                    }}
                    placeholder="Selecciona un catálogo"
                    disabled={catalogsLoading}
                    loading={catalogsLoading}
                    error={!!errors.catalogId}
                  />
                )}
              />
              {errors.catalogId && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.catalogId.message}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoría padre
              </label>
              <Controller
                control={control}
                name="parentId"
                render={({ field }) => (
                  <CategorySelect
                    options={parentOptions}
                    value={field.value ?? ""}
                    onChange={(value) => field.onChange(value)}
                    placeholder={
                      selectedCatalogId
                        ? "Selecciona una categoría padre"
                        : "Selecciona un catálogo primero"
                    }
                    disabled={!selectedCatalogId || parentLoading}
                    loading={parentLoading}
                    error={!!errors.parentId}
                  />
                )}
              />
              {errors.parentId && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.parentId.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="order"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Orden
              </label>
              <input
                {...register("order")}
                type="number"
                id="order"
                min={0}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                  errors.order
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="0"
              />
              {errors.order && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.order.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Descripción
            </label>
            <textarea
              {...register("description")}
              id="description"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                errors.description
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300"
              }`}
              placeholder="Descripción breve de la categoría"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">
                {errors.description.message}
              </p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Controller
              control={control}
              name="featured"
              render={({ field }) => (
                <label className="flex items-center space-x-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={field.value ?? false}
                    onChange={(event) => field.onChange(event.target.checked)}
                    className="h-4 w-4 text-[#282828] border-gray-300 rounded focus:ring-[#282828]"
                  />
                  <span>Marcar como categoría destacada</span>
                </label>
              )}
            />
          </div>
        </div>
      ),
    },
    {
      id: "images",
      label: "Imágenes",
      icon: <Image className="w-4 h-4" />,
      content: (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Imagen (opcional)
            </label>
            <ImageUpload
              uploadedImages={uploadedImages}
              onImagesUploaded={setUploadedImages}
              onImageRemoved={() => setUploadedImages([])}
              maxFiles={1}
              maxSizeMb={5}
            />
            {errors.image && (
              <p className="mt-1 text-sm text-red-600">
                {errors.image.message}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "seo",
      label: "SEO",
      icon: <Search className="w-4 h-4" />,
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-gray-900">
              Configuración SEO
            </h3>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={autoSeoEnabled}
                onChange={(event) => {
                  const enabled = event.target.checked;
                  setAutoSeoEnabled(enabled);
                  if (enabled) {
                    setAutoTitle(true);
                    setAutoDescription(true);
                    setAutoKeywords(true);
                  }
                }}
                className="h-4 w-4 text-[#282828] border-gray-300 rounded focus:ring-[#282828]"
              />
              <label className="text-sm text-gray-700">
                Generar SEO automáticamente
              </label>
            </div>
          </div>

          <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="seoTitle"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Título SEO (opcional)
                </label>
                <input
                  {...register("seoTitle", {
                    onChange: () => handleSeoFieldChange("seoTitle"),
                  })}
                  type="text"
                  id="seoTitle"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                    errors.seoTitle
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Título optimizado para buscadores"
                />
                {errors.seoTitle && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.seoTitle.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="seoKeywords"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Palabras clave SEO (opcional)
                </label>
                <input
                  {...register("seoKeywords", {
                    onChange: () => handleSeoFieldChange("seoKeywords"),
                  })}
                  type="text"
                  id="seoKeywords"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                    errors.seoKeywords
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="ej: pisos, interiores, porcelanato"
                />
                {errors.seoKeywords && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.seoKeywords.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="seoDescription"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Descripción SEO (opcional)
              </label>
              <textarea
                {...register("seoDescription", {
                  onChange: () => handleSeoFieldChange("seoDescription"),
                })}
                id="seoDescription"
                rows={2}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                  errors.seoDescription
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Descripción corta para buscadores"
              />
              {errors.seoDescription && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.seoDescription.message}
                </p>
              )}
            </div>
          </div>
        </div>
      ),
    },
  ];

  // Determinar qué tabs tienen errores
  const tabErrors = useMemo(() => {
    const generalFields = [
      "name",
      "catalogId",
      "parentId",
      "description",
      "image",
      "order",
      "featured",
    ];
    const seoFields = ["seoTitle", "seoDescription", "seoKeywords"];

    const hasGeneralErrors = generalFields.some(
      (field) => errors[field as keyof typeof errors]
    );
    const hasSeoErrors = seoFields.some(
      (field) => errors[field as keyof typeof errors]
    );

    return {
      general: hasGeneralErrors,
      images: false, // Las imágenes no tienen validación del formulario
      seo: hasSeoErrors,
    };
  }, [errors]);

  // Footer con botones
  const footer = (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={handleClose}
        className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors"
        disabled={isLoading}
      >
        Cancelar
      </button>
      <button
        type="submit"
        form="category-form"
        disabled={!isValid || isLoading}
        className="px-4 py-2 text-white bg-[#282828] border border-transparent rounded-md hover:bg-[#3a3a3a] focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? (
          <span className="flex items-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Guardando...
          </span>
        ) : isEditing ? (
          "Guardar cambios"
        ) : (
          "Crear Categoría"
        )}
      </button>
    </div>
  );

  return (
    <ModalWithTabs
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? "Editar Categoría" : "Crear Nueva Categoría"}
      tabs={tabs}
      size="xl"
      footer={footer}
      defaultTab="general"
      formId="category-form"
      onSubmit={handleSubmit(handleFormSubmit)}
      tabErrors={tabErrors}
    />
  );
}
